import { createContext, useLayoutEffect, useState } from "react";

// The floating meeting TOC's positioning is owned by the host page
// (MeetingPage / ProfilePage), rendered as a sibling of the right-sidebar so
// the offset is computed in ONE place instead of two coupled offsets that
// have to be kept in sync across breakpoints. MeetingPageList / ProfileOneonone
// still own the TOC's data and render <MeetingPageTOC /> via createPortal into
// the dock element here. `hasRoom` is exposed so consumers (e.g. the inline
// header chevrons in PreviousMeetingsPopoverPanel) know when the floating TOC
// actually fits beside the reading column and when they need to take over.
export type MeetingTOCDockContextValue = {
  dockEl: HTMLElement | null;
  hasRoom: boolean;
};

export const MeetingTOCDockContext = createContext<MeetingTOCDockContextValue>({
  dockEl: null,
  hasRoom: false,
});

// Minimum horizontal gutter (px) between the reading column's right edge and
// the TOC rail before it's worth showing the TOC on the side. The collapsed
// rail sits 16px inside the sidebar (the dock's right offset) and is 24px wide,
// so it needs 40px of clearance; +8px keeps a little breathing room. Below this
// the rail would overlap the notes, so the inline header chevrons take over.
const TOC_MIN_SIDE_GUTTER = 48;

// Measures whether the floating TOC has room to sit beside the reading column
// instead of overlapping it. Viewport breakpoints can't answer this on their
// own: the column's width also depends on the expanded right-sidebar and the
// responsive padding, so we measure the real geometry. Returns true when the
// gutter between the reading column's right edge and the sidebar (where the
// dock is anchored) can fit the collapsed rail.
export const useTocHasRoom = (dockEl: HTMLElement | null): boolean => {
  const [hasRoom, setHasRoom] = useState(false);

  useLayoutEffect(() => {
    // The dock is a sibling of <main> inside Layout.Container. <main> spans the
    // area left of the sidebar, and its first child is the mx-auto reading
    // column (max-w-screen-*). The dock is anchored 16px inside <main>'s right
    // edge, so the gutter we care about is (main.right - column.right).
    const mainEl = dockEl?.parentElement?.querySelector("main");
    if (!mainEl) {
      setHasRoom(false);
      return;
    }

    const measure = () => {
      const columnEl = mainEl.firstElementChild;
      if (!columnEl) {
        setHasRoom(false);
        return;
      }
      const gutter =
        mainEl.getBoundingClientRect().right -
        columnEl.getBoundingClientRect().right;
      setHasRoom(gutter >= TOC_MIN_SIDE_GUTTER);
    };

    measure();
    // <main>'s width changes on window resize and when the sidebar expands /
    // collapses (its margin shifts), both of which move the reading column.
    const observer = new ResizeObserver(measure);
    observer.observe(mainEl);
    return () => observer.disconnect();
  }, [dockEl]);

  return hasRoom;
};
